import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import {
  FormArray,
  FormBuilder,
  FormControl,
  Validators,
} from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { BehaviorSubject, EMPTY, Observable, Subject, of } from 'rxjs';
import { switchMap, catchError, filter, tap, map } from 'rxjs/operators';
import { DeliveryTargetsService } from 'src/app/content-list-management/shared/services/cmsapi/delivery-targets.service';
import { IAllowableState } from 'src/app/core/dtos/interfaces/i-allowable-state';
import { ISaveContentResponseDto } from 'src/app/core/dtos/interfaces/i-save-content-response-dto';
import { IVideoMediaDto } from 'src/app/core/dtos/interfaces/i-video-media-dto';
import { IWebsiteUrl } from 'src/app/core/dtos/interfaces/i-website-url';

import { ContentStatus } from 'src/app/shared/enums/content-status';
import { ContentTypeRouterPaths } from 'src/app/shared/enums/content-type-router-paths';
import { ContentTypes } from 'src/app/shared/enums/content-types';
import { WithContentBodyFormSection } from 'src/app/shared/mixins/with-content-body-form-section';
import { WithDeactivateGuard } from 'src/app/shared/mixins/with-deactivate-guard';
import { WithHeadlineFormSection } from 'src/app/shared/mixins/with-headline-form-section';
import { WithLanguagesFormSection } from 'src/app/shared/mixins/with-languages-form-section';
import { WithNextStateFormSection } from 'src/app/shared/mixins/with-next-states-form-section';
import { WithProcessing } from 'src/app/shared/mixins/with-processing';
import { WithWebsitesFormSection } from 'src/app/shared/mixins/with-websites-form-section';
import { DialogService } from 'src/app/shared/services/dialog.service';
import { UserSettingsService } from 'src/app/shared/services/user-settings.service';
import { FORM_PATHS } from '../constants/form-paths';
import { FORM_FIELDS } from '../constants/form-fields';
import { LanguageService } from 'src/app/content-management/shared/services/language.service';
import { ISingleSponsorContentDto } from 'src/app/core/dtos/interfaces/i-single-sponsor-content-dto';
import { SingleSponsorContentDto } from 'src/app/core/dtos/single-sponsor-content-dto';
import { SingleSponsorContentTypeService } from '../../../content/services/single-sponsor-content-type.service';
import { ICompany } from 'src/app/core/models/i-company';
import { IPerson } from 'src/app/core/models/i-person';
import { ITag, Tag } from 'src/app/core/models/i-tag';
import { ITopic } from 'src/app/core/models/i-topic';
import { WithAuthorsFormSection } from 'src/app/shared/mixins/with-authors-form-section';
import { WithPublishTimeFormSection } from 'src/app/shared/mixins/with-publish-time';
import { WithTaggingFormSection } from 'src/app/shared/mixins/with-tagging-form-section';
import { AuthorService } from 'src/app/shared/services/author.service';
import { WithContentVisibilityFormSection } from 'src/app/shared/mixins/with-visibility-section';
import { IAuthor } from 'src/app/core/models/i-author';
import { INewContentVersionResponseDto } from 'src/app/core/dtos/interfaces/i-new-content-version-response-dto';
import { SponsoredAuthor } from 'src/app/core/dtos/interfaces/i-sponsored-author-dto';
import { IImgMediaDto } from 'src/app/core/dtos/interfaces/i-img-media-dto';
import { WithSalesOrderFormSection } from 'src/app/shared/mixins/with-sales-order-form-section';
import { WithSponsoredAuthorFormSection } from 'src/app/shared/mixins/with-sponsored-authors-form-section';
import { SponsoredSection } from 'src/app/core/dtos/interfaces/i-sponsored-section-dto';
import { WithSponsoredSectionFormSection } from 'src/app/shared/mixins/with-sponsored_section-form-section';
import { WithStandfirstFormSection } from 'src/app/shared/mixins/with-standfirst-form-section';
import { UrlService } from 'src/app/shared/services/url.service';
import { SignalrService } from 'src/app/content-management/shared/services/signalr.service';
import { WithSurtitlesFormSection } from 'src/app/shared/mixins/with-surtitles-form-section';
import { ITypedSelectListItem } from 'src/app/shared-form-sections/interfaces/i-typed-select-list-item';
import { ISurtitle } from 'src/app/core/models/i-surtitle';
import { TopicService } from 'src/app/content-management/shared/services/topic.service';
import {
  handleTagSelected,
  handleTagRemoved,
  handleTagAddedAsSurtitle,
} from 'src/app/shared/utils/tagging-handler';

class SingleSponsorContentFormComponentBase {
  FORM_PATHS = FORM_PATHS;
  FORM_FIELDS = FORM_FIELDS;
  CONTENT_TYPES = ContentTypes;
  destroy$ = new Subject<boolean>();
  form = this.formBuilder.group({});

  constructor(
    public languageService: LanguageService,
    public deliveryTargetsService: DeliveryTargetsService,
    public formBuilder: FormBuilder,
    public _dialogService: DialogService,
    public authorService: AuthorService,
    public signalRService: SignalrService,
    public topicService: TopicService
  ) {}
}

const SingleSponsorContentFormComponentMixinBase = WithProcessing(
  WithDeactivateGuard(
    WithHeadlineFormSection(
      WithStandfirstFormSection(
        WithContentBodyFormSection(
          WithNextStateFormSection(
            WithWebsitesFormSection(
              WithLanguagesFormSection(
                WithAuthorsFormSection(
                  WithPublishTimeFormSection(
                    WithTaggingFormSection(
                      WithSurtitlesFormSection(
                        WithSponsoredSectionFormSection(
                          WithSalesOrderFormSection(
                            WithSponsoredAuthorFormSection(
                              WithContentVisibilityFormSection(
                                SingleSponsorContentFormComponentBase
                              )
                            )
                          )
                        )
                      )
                    )
                  )
                )
              )
            )
          )
        )
      )
    )
  )
);

@Component({
  selector: 'app-single-sponsor-content-form',
  templateUrl: './single-sponsor-content-form.component.html',
  styleUrls: ['./single-sponsor-content-form.component.scss'],
})
export class SingleSponsorContentFormComponent
  extends SingleSponsorContentFormComponentMixinBase
  implements OnInit, OnDestroy
{
  FORM_FIELDS = FORM_FIELDS;
  FORM_PATHS = FORM_PATHS;
  CONTENT_TYPES = ContentTypes;
  destroy$ = new Subject<boolean>();
  formReady = new BehaviorSubject<boolean>(false);

  isSaving = false;
  isProcessingStateChange = false;
  isEditMode = false;
  isNewSingleSponsor = false;

  id: string;
  pageHeader: string;
  singleSponsorContent$!: Observable<ISingleSponsorContentDto>;
  initialSingleSponsorContent!: ISingleSponsorContentDto;
  currentUserName: string = '';

  form = this.formBuilder.group({
    [this.FORM_FIELDS.HEADLINE]: ['', Validators.required],
    [this.FORM_FIELDS.STANDFIRST]: [''],
    [this.FORM_FIELDS.LANGUAGE]: [null],
    [this.FORM_FIELDS.BODY]: [],
    [this.FORM_FIELDS.IMGS]: this.formBuilder.array([]),
    [this.FORM_FIELDS.VIDEOS]: this.formBuilder.array([]),
    [this.FORM_FIELDS.WEBSITES]: this.formBuilder.group(
      {},
      {
        validators: [this._initWebsiteValidation()],
      }
    ),
    [this.FORM_FIELDS.INTERNAL_INFORMATION]: this.formBuilder.group({
      [this.FORM_FIELDS.LIVE_URLS]: this.formBuilder.array([]),
      [this.FORM_FIELDS.CONTENT_IDS]: [],
    }),
    [this.FORM_FIELDS.ARTICLE_VERSION_LIST]: this.formBuilder.array([]),
    [this.FORM_FIELDS.NEXT_STATES]: [],
    [this.FORM_FIELDS.PUBLISH]: this.formBuilder.group(
      {
        [this.FORM_FIELDS.DATE]: [],
        [this.FORM_FIELDS.TIMEZONE]: [],
        [this.FORM_FIELDS.SHOW_PUBLISH_TIME]: [false],
      },
      {
        validators: [this._initPublishTimeValidation()],
      }
    ),
    [this.FORM_FIELDS.SURTITLES]: this.formBuilder.array([]),
    [this.FORM_FIELDS.EDITION_LANGUAGE]: ['en'],
    [this.FORM_FIELDS.TYPED_TAGS]: this.formBuilder.array([]),
    [this.FORM_FIELDS.TAGS]: this.formBuilder.array([]),
    [this.FORM_FIELDS.TOPICS]: this.formBuilder.array([]),
    [this.FORM_FIELDS.TOPICS_WITH_TRANSLATIONS]: this.formBuilder.array([]),
    [this.FORM_FIELDS.COMPANIES]: this.formBuilder.array([]),
    [this.FORM_FIELDS.PEOPLE]: this.formBuilder.array([]),
    [this.FORM_FIELDS.SELECTED_AUTHORS]: this.formBuilder.array([]),
    [this.FORM_FIELDS.RESTRICTED_TO_REGISTERED_USERS]: [null],
    [this.FORM_FIELDS.NOT_FOR_GOOGLE_NEWS_SITEMAPS]: [null],
    [this.FORM_FIELDS.NOT_FOR_NEWS_FEED]: [null],
    [this.FORM_FIELDS.HIDE_THIS_ARTICLE]: [null],
    [this.FORM_FIELDS.PAYWALL_PREMIUM_CONTENT]: [null],
    [this.FORM_FIELDS.NOT_FOR_COMPETITORS]: [null],
    [this.FORM_FIELDS.SPONSORED_HEADLINE]: ['', Validators.required],
    [this.FORM_FIELDS.SPONSORED_STANDFIRST]: [''],
    [this.FORM_FIELDS.SPONSORED_BODY]: [''],
    [this.FORM_FIELDS.SPONSORED_IMGS]: this.formBuilder.array([]),
    [this.FORM_FIELDS.SPONSORED_VIDEOS]: this.formBuilder.array([]),
    [this.FORM_FIELDS.SPONSORED_CLIENT]: [''],
    [this.FORM_FIELDS.SPONSORED_SALES_ORDER]: [''],
    [this.FORM_FIELDS.SPONSORED_MAIN_IMAGE]: this.formBuilder.array<
      IImgMediaDto[]
    >([]),
    [this.FORM_FIELDS.SPONSORED_MAIN_VIDEO]: this.formBuilder.array<
      IVideoMediaDto[]
    >([]),
    [this.FORM_FIELDS.SPONSORED_AUTHORS]: this.formBuilder.array([]),
  });

  @HostListener('window:beforeunload', ['$event'])
  beforeUnload(event: Event): void {
    this.handleBeforeUnload(event);
  }

  constructor(
    public formBuilder: FormBuilder,
    public languageService: LanguageService,
    public deliveryTargetsService: DeliveryTargetsService,
    public singleSponsorService: SingleSponsorContentTypeService,
    public signalRService: SignalrService,
    public topicService: TopicService,
    public _dialogService: DialogService,
    private _activatedRoute: ActivatedRoute,
    private _snackBar: MatSnackBar,
    private _router: Router,
    private _userSettingsService: UserSettingsService,
    public authorService: AuthorService,
    public urlService: UrlService
  ) {
    super(
      languageService,
      deliveryTargetsService,
      formBuilder,
      _dialogService,
      authorService,
      signalRService,
      topicService
    );
  }

  ngOnInit(): void {
    this._routerUrlSub();

    if (this.isNewSingleSponsor) {
      this.pageHeader = 'Create Single Sponsor';
      this.initialSingleSponsorContent = new SingleSponsorContentDto({});
      this.initialSingleSponsorContent.dynamicData.allowableNextStates = [];
      this.initialSingleSponsorContent.dynamicData.versions = [];
      this.initialSingleSponsorContent.dynamicData.liveUrls = [];
      this.initialSingleSponsorContent.dynamicData.previewUrls = [];
      this.initialSingleSponsorContent.sponsoredSection = new SponsoredSection(
        {}
      );

      this._userSettingsService.getForCurrentUser$().subscribe((x) => {
        this.initialSingleSponsorContent.authors =
          x.defaultArticleAuthors ?? [];
        this.initialSingleSponsorContent.languageCode = x.defaultLanguageCode;
        this.initialSingleSponsorContent.deliveryTargetCodes =
          x.defaultDeliveryTargetCodes ?? [];
        this._initForm();
      });
    } else {
      this.pageHeader = 'Edit Single Sponsor';
      this._routeParamsSub();
    }

    if (!this.isEditMode && !this.isNewSingleSponsor) {
      this.form.disable();
    }

    this._userSettingsService.getCurrentUserName$().subscribe((username) => {
      this.currentUserName = username;
    });
  }

  private _routerUrlSub(): void {
    this._activatedRoute.url.subscribe((path) => {
      const segments = path.map((segment) => segment.path);
      this.isEditMode =
        segments.length > 0 &&
        (segments.includes('edit') || segments.includes('new'));

      this.isNewSingleSponsor = segments.length > 0 && segments.includes('new');
    });
  }

  markAsPristine() {
    // Existing content bodies have some weird formating in them which the new text editor automatically fixes
    // This causes the form body to be dirtied with no user input
    // So we mark the form as pristine again and update the initial body value with the auto-formatted one
    this.initialSingleSponsorContent.body = this.form.get(
      FORM_PATHS.BODY()
    ).value;
    this.form.markAsPristine();
  }

  private _routeParamsSub(): void {
    this._activatedRoute.paramMap
      .pipe(
        filter((paramMaps) => {
          return !!paramMaps.get('id');
        }),
        switchMap((paramsMap) => {
          this.id = paramsMap.get('id');
          return this.process$(this.getSingleSponsorContent$(this.id));
        })
      )
      .pipe(
        catchError((error) => {
          if (error.status === 404) {
            this._dialogService.openErrorDialogAndNavigate(
              {
                title: 'Single Sponsor content not found',
                message:
                  'The single sponsor content was not found. It may have been deleted.',
              },
              'dashboard'
            );
          } else {
            this._dialogService.openErrorDialogAndNavigate(
              {
                message:
                  'An error has occurred loading the single sponsor content. Please contact IT.',
              },
              'dashboard'
            );
          }

          return of(null);
        })
      )
      .subscribe({
        next: (singleSponsorContentDto: ISingleSponsorContentDto) => {
          this.initialSingleSponsorContent = singleSponsorContentDto;

          this.setEditMode(singleSponsorContentDto.dynamicData.canEdit);
          this._initForm();
          this._findEditionLanguage(this.getCurrentWebsites());
        },
      });
  }

  private setEditMode(newEditModeValue) {
    if (newEditModeValue !== this.isEditMode) {
      newEditModeValue
        ? this.redirectToEditContent(this.id)
        : this.redirectToReadOnlyContent(this.id);
      return;
    }
    this.isEditMode = newEditModeValue;

    this.isEditMode ? this.form.enable() : this.form.disable();
  }

  private redirectToReadOnlyContent(id: string) {
    this._router.navigate([
      '/content',
      ContentTypeRouterPaths[ContentTypes.SINGLE_SPONSOR],
      id,
    ]);
  }

  private redirectToEditContent(id: string) {
    this._router.navigate([
      '/content',
      ContentTypeRouterPaths[ContentTypes.SINGLE_SPONSOR],
      id,
      'edit',
    ]);
  }

  private _initForm(): void {
    this._initDeliveryTargets(this.getCurrentWebsites, this.getCurrentUrls);
    this._initHeadline(this.initialSingleSponsorContent.headline);
    this._initStandfirst(this.initialSingleSponsorContent.standfirst);
    this._initLanguages(this.getCurrentSelectedLanguage);
    this._initNextStates(
      this.initialSingleSponsorContent.dynamicData.allowableNextStates
    );
    this._initContentBody(
      this.initialSingleSponsorContent.body,
      this.initialSingleSponsorContent.bodyImages,
      this.initialSingleSponsorContent.bodyVideos
    );
    this._initAuthors(this.initialSingleSponsorContent.authors);
    this._initTagging(
      this.initialSingleSponsorContent.tags,
      this.initialSingleSponsorContent.topics,
      this.initialSingleSponsorContent.relatedCompanies,
      this.initialSingleSponsorContent.relatedPeople,
      this.topicService
    );
    this._initSurtitles(
      this.initialSingleSponsorContent.surtitlesDetailed,
      this.initialSingleSponsorContent.tags,
      this.initialSingleSponsorContent.topics,
      this.initialSingleSponsorContent.relatedCompanies,
      this.initialSingleSponsorContent.relatedPeople
    );
    this._initPublishTime(
      this.initialSingleSponsorContent.presetPublishDate,
      this.initialSingleSponsorContent.timezoneIdentifier
    );

    this._initContentVisibility(
      this.initialSingleSponsorContent.restrictToRegisteredUsers,
      this.initialSingleSponsorContent.notForGoogleNewsSitemaps,
      this.initialSingleSponsorContent.notForNewsFeeds,
      this.initialSingleSponsorContent.hideThisArticle,
      this.initialSingleSponsorContent.paywallPremiumContent,
      this.initialSingleSponsorContent.notForCompetitors
    );

    this._initSponsoredSection(
      this.initialSingleSponsorContent.sponsoredSection.headline,
      this.initialSingleSponsorContent.sponsoredSection.standfirst,
      this.initialSingleSponsorContent.sponsoredSection.body,
      this.initialSingleSponsorContent.sponsoredSection.bodyImages,
      this.initialSingleSponsorContent.sponsoredSection.bodyVideos,
      this.initialSingleSponsorContent.sponsoredSection.mainImage,
      this.initialSingleSponsorContent.sponsoredSection.mainVideo
    );

    this._initSalesOrder(
      this.initialSingleSponsorContent.sponsoredSection.salesOrder,
      this.initialSingleSponsorContent.sponsoredSection.client
    );

    this._initSponsoredAuthor(
      this.initialSingleSponsorContent.sponsoredSection.sponsoredAuthors
    );

    this._initFormValueChanges();
    this.formReady.next(true);
  }

  private _isVersionId(contentIdToCheck: string): boolean {
    return contentIdToCheck.includes('SingleSponsor');
  }

  private _initFormValueChanges(): void {
    this._initLanguageValueChanges(this.getCurrentSelectedLanguage);
    this._initWebsitesValueChanges(this.getCurrentWebsites);
    this._initHeadlineValueChanges(this.getHeadline);
    this._initStandfirstValueChanges(this.getStandfirst);
    this._initContentBodyValueChanges(
      this.getBody,
      this.getBodyImgs,
      this.getBodyVideos
    );
    this._initNextStatesValueChanges(this.getNextStates);
    this._initPublishTimeValueChanges(
      this.getPresetPublishTime,
      this.getTimezone
    );
    this._initTaggingValueChanges(
      this.getTags,
      this.getTopics,
      this.getCompanies,
      this.getPeople
    );
    this._initSurtitlesValueChanges(this.getSurtitles);
    this._initContentVisibilityValueChanges(
      this.getRestrictToRegisteredUsers,
      this.getNotForGoogleNewsSitemaps,
      this.getNotForNewsFeeds,
      this.getHideThisArticle,
      this.getPaywallPremiumContent,
      this.getNotForCompetitors
    );
    this._initSelectedAuthorsValueChanges(this.getSelectedAuthors);

    this._initSponsoredSectionValueChanges(
      this.getSponsoredHeadline,
      this.getSponsoredStandfirst,
      this.getSponsoredBody,
      this.getSponsoredImages,
      this.getSponsoredVideos,
      this.getSponsoredMainImage,
      this.getSponsoredMainVideo
    );

    this._initSalesOrderValueChanges(this.getSalesOrder, this.getClient);

    this._initSponsoredAuthorValueChanges(this.getSponsoredAuthors);
  }

  private _updateForms(): void {
    this._updateDeliveryTargets(this.getCurrentWebsites, this.getCurrentUrls);
  }

  getCurrentSelectedLanguage = () => {
    return this.initialSingleSponsorContent.languageCode;
  };

  getCurrentWebsites = (): string[] | null => {
    return this.initialSingleSponsorContent.deliveryTargetCodes;
  };

  getCurrentUrls = (): IWebsiteUrl[] | null => {
    if (this.initialSingleSponsorContent.status != ContentStatus.PUBLISHED) {
      return this.initialSingleSponsorContent.deliveryTargetCodes.map(
        (targetCode) => {
          return {
            deliveryTargetCode: targetCode,
            url: this.urlService.getPreviewUrl(
              this.initialSingleSponsorContent.id,
              targetCode
            ),
          };
        }
      );
    }
    return this.initialSingleSponsorContent.dynamicData.liveUrls;
  };

  getHeadline = (): string | null => {
    return this.initialSingleSponsorContent.headline;
  };

  getStandfirst = (): string | null => {
    return this.initialSingleSponsorContent.standfirst;
  };

  getBody = (): string | null => {
    return this.initialSingleSponsorContent.body;
  };

  getBodyImgs = (): IImgMediaDto[] | null => {
    return this.initialSingleSponsorContent.bodyImages;
  };

  getBodyVideos = (): IVideoMediaDto[] | null => {
    return this.initialSingleSponsorContent.bodyVideos;
  };

  getNextStates = (): IAllowableState[] | null => {
    return this.initialSingleSponsorContent.dynamicData.allowableNextStates;
  };
  getPresetPublishTime = (): Date | null => {
    return this.initialSingleSponsorContent.presetPublishDate;
  };

  getTimezone = (): string | null => {
    return this.initialSingleSponsorContent.timezoneIdentifier;
  };

  getSurtitles = (): ISurtitle[] | null => {
    return this.initialSingleSponsorContent.surtitlesDetailed;
  };

  getTags = (): ITag[] | null => {
    return this.initialSingleSponsorContent.tags;
  };

  getTopics = (): ITopic[] | null => {
    return this.initialSingleSponsorContent.topics;
  };

  getCompanies = (): ICompany[] | null => {
    return this.initialSingleSponsorContent.relatedCompanies;
  };

  getPeople = (): IPerson[] | null => {
    return this.initialSingleSponsorContent.relatedPeople;
  };

  getRestrictToRegisteredUsers = (): boolean | null => {
    return this.initialSingleSponsorContent.restrictToRegisteredUsers;
  };

  getNotForGoogleNewsSitemaps = (): boolean | null => {
    return this.initialSingleSponsorContent.notForGoogleNewsSitemaps;
  };

  getNotForNewsFeeds = (): boolean | null => {
    return this.initialSingleSponsorContent.notForNewsFeeds;
  };

  getHideThisArticle = (): boolean | null => {
    return this.initialSingleSponsorContent.hideThisArticle;
  };
  getPaywallPremiumContent = (): boolean | null => {
    return this.initialSingleSponsorContent.paywallPremiumContent;
  };
  getNotForCompetitors = (): boolean | null => {
    return this.initialSingleSponsorContent.notForCompetitors;
  };
  getSelectedAuthors = (): IAuthor[] | null => {
    return this.initialSingleSponsorContent.authors;
  };

  getSponsoredHeadline = (): string | null => {
    return this.initialSingleSponsorContent.sponsoredSection.headline;
  };

  getSponsoredStandfirst = (): string | null => {
    return this.initialSingleSponsorContent.sponsoredSection.standfirst;
  };

  getSponsoredMainImage = (): IImgMediaDto | null => {
    return this.initialSingleSponsorContent.sponsoredSection.mainImage;
  };

  getSponsoredMainVideo = (): IVideoMediaDto | null => {
    return this.initialSingleSponsorContent.sponsoredSection.mainVideo;
  };

  getSponsoredBody = (): string | null => {
    return this.initialSingleSponsorContent.sponsoredSection.body;
  };

  getSponsoredImages = (): IImgMediaDto[] | null => {
    return this.initialSingleSponsorContent.sponsoredSection.bodyImages;
  };

  getSponsoredVideos = (): IVideoMediaDto[] | null => {
    return this.initialSingleSponsorContent.sponsoredSection.bodyVideos;
  };

  getSalesOrder = (): string | null => {
    return this.initialSingleSponsorContent.sponsoredSection.salesOrder;
  };

  getClient = (): string | null => {
    return this.initialSingleSponsorContent.sponsoredSection.client;
  };

  getSponsoredAuthors = (): SponsoredAuthor[] | null => {
    return this.initialSingleSponsorContent.sponsoredSection.sponsoredAuthors;
  };

  createNewVersion(): void {
    this.isProcessingStateChange = true;
    this.singleSponsorService
      .createVersion$(
        this.initialSingleSponsorContent.contentId,
        this.initialSingleSponsorContent.id
      )
      .pipe(
        catchError(() => {
          this._dialogService.openErrorDialog({
            message:
              'An error has occurred whilst creating a new version of the single sponsor content. Please contact IT.',
          });
          this.isProcessingStateChange = false;
          return of(null);
        })
      )
      .subscribe(() => {
        this._snackBar.open(
          `New single sponsor content version created successfully`,
          'Save',
          {
            duration: 5 * 1000,
          }
        );
        this.isProcessingStateChange = false;
        this.redirectToEditContent(this.initialSingleSponsorContent.contentId);
      });
  }

  onUpdateState(stateAction: ContentStatus): void {
    const obs$ =
      stateAction === ContentStatus.PUBLISHED
        ? this._dialogService
            .openConfirmationDialog({
              title: 'Publish single sponsor content?',
              message:
                'This will make it live on the websites you have selected.',
              confirmLabel: 'Yes',
              cancelLabel: 'No',
            })
            .afterClosed()
            .pipe(
              filter((dialogResponse) => !!dialogResponse),
              switchMap((_) => {
                return this.performStateChange(stateAction);
              })
            )
        : this.performStateChange(stateAction);

    obs$.subscribe();
  }

  private performStateChange(
    stateAction: ContentStatus
  ): Observable<ISingleSponsorContentDto> {
    let action = stateAction.toString();
    this.isProcessingStateChange = true;
    return this.process$(
      this.singleSponsorService.updateState$(
        this.initialSingleSponsorContent.id,
        action
      )
    ).pipe(
      catchError(() => {
        this._dialogService.openErrorDialog({
          message:
            'An error has occurred whilst saving the page. Please contact IT.',
        });
        this.isProcessingStateChange = false;
        return of(null);
      }),
      switchMap((contentStateChange) => {
        this.isProcessingStateChange = false;
        this._snackBar.open(
          `Single sponsor content moved to ${stateAction} successfully`,
          null,
          {
            duration: 5 * 1000,
          }
        );

        if (contentStateChange.currentStateEditable !== this.isEditMode) {
          this.setEditMode(contentStateChange.currentStateEditable);
          return of(null);
        } else {
          return this.getSingleSponsorContent$(
            this.initialSingleSponsorContent.id
          );
        }
      }),
      tap((refreshedDto: ISingleSponsorContentDto) => {
        this.initialSingleSponsorContent = refreshedDto;
        this.markAsPristine();
      })
    );
  }

  onSave(): void {
    if (this.isNewSingleSponsor) {
      this.isSaving = true;
      this.createNewSingleSponsor();
    } else {
      let missingWebsites = this.getCurrentWebsites().filter(
        (website) => !this._buildDeliveryTargetCodes().includes(website)
      );
      const obs$ =
        missingWebsites.length > 0
          ? this.deliveryTargets$.pipe(
              map((dt) =>
                dt
                  .filter((item) => missingWebsites.includes(item.id))
                  .map((item) => item.label)
              ),
              switchMap((displayList) => {
                return this._dialogService
                  .openConfirmationDialog({
                    title: 'Save single sponsor content?',
                    message:
                      `This change will remove content from the following websites <br><br>` +
                      `${displayList.join(', ')}<br><br>` +
                      `Are you sure you want to continue to save?<br>` +
                      `Note: The links in emails will break for removed sites`,
                    confirmLabel: 'Yes',
                    cancelLabel: 'No',
                  })
                  .afterClosed()
                  .pipe(filter((dialogResponse) => !!dialogResponse));
              }),
              switchMap((_) => {
                return this.performSingleSponsorSave$();
              })
            )
          : this.performSingleSponsorSave$();

      obs$.subscribe();
    }
  }

  private performSingleSponsorSave$(): Observable<ISingleSponsorContentDto> {
    let newSingleSponsorContent = this.buildSingleSponsor();
    this.isSaving = true;
    return this.singleSponsorService
      .update$(
        this.initialSingleSponsorContent.contentId,
        newSingleSponsorContent
      )
      .pipe(
        catchError(() => {
          this._dialogService.openErrorDialog({
            message:
              'An error has occurred whilst saving the page. Please contact IT.',
          });
          this.isSaving = false;
          return of(null);
        }),
        switchMap(() => {
          return this.getSingleSponsorContent$(
            this.initialSingleSponsorContent.contentId
          );
        }),
        tap((refreshedDto) => {
          this._snackBar.open(
            'Single sponsor content saved successfully',
            null,
            {
              duration: 5 * 1000,
            }
          );
          this.initialSingleSponsorContent = refreshedDto;
          this.form.markAsPristine();
          this.isSaving = false;
          this._updateForms();
          this.signalRService.userSavedPage(
            this.currentUserName,
            refreshedDto.contentId
          );
        })
      );
  }

  createNewSingleSponsor() {
    let newSingleSponsorContent = this.buildSingleSponsor();

    if (newSingleSponsorContent.deliveryTargetCodes.length === 0) {
      this._dialogService.openErrorDialog({
        message: 'Website is required in order to save',
      });
    } else {
      this.isSaving = true;
      this.singleSponsorService
        .create$(newSingleSponsorContent)
        .pipe(
          catchError(() => {
            this._dialogService.openErrorDialog({
              message:
                'An error has occurred whilst saving the page. Please contact IT.',
            });
            this.isSaving = false;
            return of(null);
          })
        )
        .subscribe((x: ISaveContentResponseDto | null) => {
          if (!x) {
            return;
          }
          this.form.markAsPristine();
          this._router.navigate([
            '/content',
            ContentTypeRouterPaths[ContentTypes.SINGLE_SPONSOR],
            x.contentId,
            'edit',
          ]);
        });
    }
  }

  deleteSingleSponsor() {
    this._dialogService
      .openConfirmationDialog({
        title: 'Delete single sponsor content?',
        message:
          'This will delete the current single sponsor content and all its versions. Are you sure you want to do this?',
        confirmLabel: 'Ok',
      })
      .afterClosed()
      .pipe(
        filter((dialogResponse) => !!dialogResponse),
        switchMap(() => {
          return this.singleSponsorService
            .delete$(this.initialSingleSponsorContent.contentId)
            .pipe(
              catchError(() => {
                this._dialogService.openErrorDialog({
                  message:
                    'An error has occurred whilst deleting the single sponsor content. Please contact IT.',
                });
                return of(null);
              })
            );
        })
      )
      .subscribe({
        next: () => {
          this._router.navigate(['/dashboard']);
        },
      });
  }

  spikeSingleSponsor() {
    this._dialogService
      .openConfirmationDialog({
        title: 'Spike single sponsor?',
        message:
          'This will delete the draft single sponsor and spike the current single sponsor. Are you sure you want to do this?',
        confirmLabel: 'Yes',
      })
      .afterClosed()
      .pipe(
        filter((dialogResponse) => !!dialogResponse),
        switchMap(() => {
          return this._dialogService
            .openErrorDialog({
              title: 'Remove from Google',
              message:
                'Please remove the URLs (for each site the single sponsor was published to) from Google by visiting https://www.google.com/webmasters/tools/url-removal',
              confirmLabel: 'Ok',
            })
            .afterClosed()
            .pipe(
              switchMap(() => {
                return this.singleSponsorService
                  .spike$(this.initialSingleSponsorContent.contentId)
                  .pipe(
                    catchError(() => {
                      this._dialogService.openErrorDialog({
                        message:
                          'An error has occurred whilst deleting the single sponsor. Please contact IT.',
                      });
                      return EMPTY;
                    })
                  );
              })
            );
        })
      )
      .subscribe({
        next: () => {
          this._router.navigate(['/dashboard']);
        },
      });
  }

  deleteUnpublishedDraft() {
    this._dialogService
      .openConfirmationDialog({
        title: 'Delete unpublished draft version?',
        message:
          'This will delete the current unpublished draft version. Are you sure you want to do this?',
        confirmLabel: 'Delete',
        cancelLabel: 'Cancel',
      })
      .afterClosed()
      .pipe(
        filter((dialogResponse) => !!dialogResponse),
        switchMap((_) => {
          return this.singleSponsorService
            .deleteVersion$(
              this.initialSingleSponsorContent.contentId,
              this.initialSingleSponsorContent.id
            )
            .pipe(
              catchError(() => {
                this._dialogService.openErrorDialog({
                  message:
                    'An error has occurred whilst deleting the page. Please contact IT.',
                });
                return EMPTY;
              })
            );
        })
      )
      .subscribe({
        next: () => {
          this._router.navigate(['/dashboard']);
        },
      });
  }

  createNewVersionWithCurrentStateCheck$(): Observable<INewContentVersionResponseDto> {
    return this.initialSingleSponsorContent.dynamicData.currentDraftVersion
      ? this._dialogService
          .openConfirmationDialog({
            title: 'Create new version from single sponsor?',
            message:
              'This will create a new version and delete the current draft. Are you sure you want to do this?',
            confirmLabel: 'Yes',
            cancelLabel: 'No',
          })
          .afterClosed()
          .pipe(
            filter((dialogResponse) => !!dialogResponse),
            switchMap((_) => {
              return this.deleteAndCreateNewVersion$(
                this.initialSingleSponsorContent.contentId,
                this.initialSingleSponsorContent.id,
                this.initialSingleSponsorContent.dynamicData.currentDraftVersion
                  .id
              );
            })
          )
      : this.createNewVersion$(
          this.initialSingleSponsorContent.contentId,
          this.initialSingleSponsorContent.id
        );
  }

  onReactivateSingleSponsor(): void {
    let obs$;
    this.isProcessingStateChange = true;
    if (this.isPreviouslyPublishedVersion()) {
      obs$ = this.handlePreviouslyPublishedVersion$();
    } else {
      obs$ = this.createNewVersionWithCurrentStateCheck$();
    }

    obs$.subscribe({
      next: () => {
        this._snackBar.open(`Single sponsor reactivated successfully`, null, {
          duration: 5 * 1000,
        });
        this.redirectToEditContent(this.initialSingleSponsorContent.contentId);
      },
      complete: () => (this.isProcessingStateChange = false),
    });
  }

  private handlePreviouslyPublishedVersion$(): Observable<INewContentVersionResponseDto> {
    return this.singleSponsorService
      .getByVersionId$(
        this.initialSingleSponsorContent.dynamicData.latestPublishedVersionId
      )
      .pipe(
        switchMap((publishedVersion) => {
          const missingWebsites = this.getMissingWebsites(publishedVersion);
          return missingWebsites.length > 0
            ? this.confirmReactivateWithWebsites$(missingWebsites)
            : this.confirmReactivate$();
        }),
        filter((dialogResponse) => !!dialogResponse),
        switchMap((_) => this.createNewVersionWithCurrentStateCheck$())
      );
  }

  private confirmReactivateWithWebsites$(
    missingWebsites: string[]
  ): Observable<boolean> {
    return this.deliveryTargets$.pipe(
      map((deliveryTargets) =>
        deliveryTargets
          .filter((target) => missingWebsites.includes(target.id))
          .map((target) => target.label)
      ),
      switchMap((displayList) =>
        this._dialogService
          .openConfirmationDialog({
            title: 'Reactivate single sponsor?',
            message: `Are you sure? You are reactivating an old version of this content. This will overwrite the currently live published version as well as remove these websites:<br><br>${displayList.join(
              ', '
            )}<br><br>Note: The links in emails will break for removed sites`,
            confirmLabel: 'Reactivate',
            cancelLabel: 'Cancel',
          })
          .afterClosed()
      )
    );
  }

  private confirmReactivate$(): Observable<boolean> {
    return this._dialogService
      .openConfirmationDialog({
        title: 'Reactivate single sponsor?',
        message: `Are you sure? You are reactivating an old version of this content. This will overwrite the currently live published version.`,
        confirmLabel: 'Reactivate',
        cancelLabel: 'Cancel',
      })
      .afterClosed();
  }

  private getMissingWebsites(
    publishedVersion: ISingleSponsorContentDto
  ): string[] {
    return publishedVersion.deliveryTargetCodes.filter(
      (website) => !this._buildDeliveryTargetCodes().includes(website)
    );
  }

  onTagItemSelected($event) {
    const typedTags = this.form.get(FORM_PATHS.TYPED_TAGS()) as FormArray;

    handleTagSelected($event, typedTags);
  }

  onTagItemRemoved($event: ITypedSelectListItem) {
    const typedTags = this.form.get(FORM_PATHS.TYPED_TAGS()) as FormArray;
    const surtitleControl: FormArray<FormControl<ISurtitle>> = this.form.get(
      FORM_PATHS.SURTITLES()
    ) as FormArray;

    handleTagRemoved($event, typedTags, surtitleControl);
  }

  onSurtitlePromoted($event: ITypedSelectListItem) {
    const surtitleControl: FormArray<FormControl<ISurtitle>> = this.form.get(
      FORM_PATHS.SURTITLES()
    ) as FormArray;

    handleTagAddedAsSurtitle($event, surtitleControl);
  }
  onEditionAdded(): void {
    this._findEditionLanguage(this._buildDeliveryTargetCodes());
  }

  onEditionRemoved(): void {
    this._findEditionLanguage(this._buildDeliveryTargetCodes());
  }

  private deleteAndCreateNewVersion$(
    contentId: string,
    versionToCopyId: string,
    versionToDeleteId: string
  ): Observable<INewContentVersionResponseDto> {
    return this.singleSponsorService
      .deleteVersion$(contentId, versionToDeleteId)
      .pipe(
        catchError(() => {
          this._dialogService.openErrorDialog({
            message:
              'An error has occurred whilst deleting an single sponsor version. Please contact IT.',
          });
          this.isProcessingStateChange = false;
          return EMPTY;
        }),
        switchMap(() => this.createNewVersion$(contentId, versionToCopyId))
      );
  }

  private createNewVersion$(
    contentId: string,
    versionId: string
  ): Observable<INewContentVersionResponseDto> {
    return this.singleSponsorService.createVersion$(contentId, versionId).pipe(
      catchError(() => {
        this._dialogService.openErrorDialog({
          message:
            'An error has occurred whilst creating a new version of the single sponsor. Please contact IT.',
        });
        this.isProcessingStateChange = false;
        return EMPTY;
      })
    );
  }

  private isPreviouslyPublishedVersion(): boolean {
    return (
      this.initialSingleSponsorContent.status === ContentStatus.PUBLISHED &&
      this.initialSingleSponsorContent?.dynamicData
        ?.latestPublishedVersionId !== this.initialSingleSponsorContent?.id
    );
  }

  private getSingleSponsorContent$(id: string) {
    if (this._isVersionId(id)) {
      this.singleSponsorContent$ = this.process$(
        this.singleSponsorService.getByVersionId$(id)
      );
    } else {
      this.singleSponsorContent$ = this.process$(
        this.singleSponsorService.getById$(id)
      );
    }

    return this.singleSponsorContent$;
  }

  private buildSingleSponsor(): SingleSponsorContentDto {
    const newSingleSponsorContent = new SingleSponsorContentDto({});
    Object.assign(newSingleSponsorContent, this.initialSingleSponsorContent);
    newSingleSponsorContent.headline = this._buildHeadline();
    newSingleSponsorContent.standfirst = this._buildStandfirst();
    newSingleSponsorContent.body = this._buildBody();
    newSingleSponsorContent.bodyImages = this._buildBodyImages();
    newSingleSponsorContent.bodyVideos = this._buildBodyVideos();
    newSingleSponsorContent.deliveryTargetCodes =
      this._buildDeliveryTargetCodes();
    newSingleSponsorContent.languageCode = this._buildLanguage();
    newSingleSponsorContent.surtitlesDetailed = this._buildSurtitles();
    newSingleSponsorContent.topics = this._buildTopics();
    newSingleSponsorContent.tags = this._buildTags();
    newSingleSponsorContent.authors = this._buildSelectedAuthors();
    newSingleSponsorContent.presetPublishDate = this._buildPresetPublishTime();
    newSingleSponsorContent.timezoneIdentifier = this._buildTimezone();
    newSingleSponsorContent.restrictToRegisteredUsers =
      this._buildRestrictToRegisteredUsers();
    newSingleSponsorContent.notForGoogleNewsSitemaps =
      this._buildNotForGoogleNewsSitemaps();
    newSingleSponsorContent.notForNewsFeeds = this._buildNotForNewsFeeds();
    newSingleSponsorContent.hideThisArticle = this._buildHideThisArticle();
    newSingleSponsorContent.paywallPremiumContent =
      this._buildPaywallPremiumContent();
    newSingleSponsorContent.notForCompetitors = this._buildNotForCompetitors();
    newSingleSponsorContent.sponsoredSection.headline =
      this._buildSponsoredHeadline();
    newSingleSponsorContent.sponsoredSection.standfirst =
      this._buildSponsoredStandfirst();
    newSingleSponsorContent.sponsoredSection.body = this._buildSponsoredBody();
    newSingleSponsorContent.sponsoredSection.bodyImages =
      this._buildSponsoredBodyImages();
    newSingleSponsorContent.sponsoredSection.bodyVideos =
      this._buildSponsoredBodyVideos();
    newSingleSponsorContent.sponsoredSection.mainImage =
      this._buildSponsoredMainImage();
    newSingleSponsorContent.sponsoredSection.mainVideo =
      this._buildSponsoredMainVideo();
    newSingleSponsorContent.sponsoredSection.client = this._buildClient();
    newSingleSponsorContent.sponsoredSection.salesOrder =
      this._buildSalesOrder();
    newSingleSponsorContent.sponsoredSection.sponsoredAuthors =
      this._buildSponsoredAuthor();

    return newSingleSponsorContent;
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }
}
